import React, { useState, useEffect } from 'react';
import { Package, TrendingUp, AlertCircle, BarChart3, DollarSign, Box } from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Select } from '../../ui/Select';

interface ABCItem {
  skuId: string;
  skuName: string;
  category: string;
  class: 'A' | 'B' | 'C';
  salesValue: number;
  salesVolume: number;
  stockValue: number;
  cumulativePercentage: number;
  turnover: number;
  margin: number;
  recommendation: string;
}

const ABCAnalysis: React.FC = () => {
  const [abcItems, setAbcItems] = useState<ABCItem[]>([]);
  const [selectedClass, setSelectedClass] = useState<'all' | 'A' | 'B' | 'C'>('all');
  const [sortBy, setSortBy] = useState<'value' | 'volume' | 'turnover'>('value');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // ABC分析データを生成
  const generateABCData = () => {
    const items: ABCItem[] = [
      { id: 'SKU001', name: 'コカ・コーラ 500ml', cat: '飲料', value: 2800000, volume: 15000, stock: 350000 },
      { id: 'SKU002', name: 'ポテトチップス うすしお', cat: '菓子', value: 2100000, volume: 8500, stock: 280000 },
      { id: 'SKU003', name: '牛乳 1L', cat: '日配品', value: 1950000, volume: 12000, stock: 150000 },
      { id: 'SKU004', name: 'サントリー天然水 2L', cat: '飲料', value: 1800000, volume: 10000, stock: 200000 },
      { id: 'SKU005', name: 'チョコレート', cat: '菓子', value: 1650000, volume: 5500, stock: 180000 },
      { id: 'SKU006', name: '食パン 6枚切', cat: '日配品', value: 1450000, volume: 8000, stock: 80000 },
      { id: 'SKU007', name: 'ヨーグルト 4個パック', cat: '日配品', value: 1200000, volume: 6000, stock: 120000 },
      { id: 'SKU008', name: '冷凍餃子 12個入', cat: '冷凍食品', value: 980000, volume: 3500, stock: 150000 },
      { id: 'SKU009', name: 'バナナ 1房', cat: '生鮮', value: 850000, volume: 7000, stock: 60000 },
      { id: 'SKU010', name: 'トマト 3個入', cat: '生鮮', value: 720000, volume: 4000, stock: 50000 },
      { id: 'SKU011', name: '冷凍チャーハン', cat: '冷凍食品', value: 650000, volume: 2800, stock: 100000 },
      { id: 'SKU012', name: 'せんべい', cat: '菓子', value: 480000, volume: 2000, stock: 80000 },
      { id: 'SKU013', name: 'ティッシュペーパー', cat: '日用品', value: 350000, volume: 1500, stock: 70000 },
      { id: 'SKU014', name: '洗剤', cat: '日用品', value: 280000, volume: 800, stock: 60000 },
      { id: 'SKU015', name: 'シャンプー', cat: '日用品', value: 150000, volume: 500, stock: 40000 },
    ];
    
    // ソート
    const sorted = [...items].sort((a, b) => {
      switch (sortBy) {
        case 'volume': return b.volume - a.volume;
        case 'turnover': return (b.value / b.stock) - (a.value / a.stock);
        default: return b.value - a.value;
      }
    });
    
    // 累積計算とクラス分け
    const totalValue = sorted.reduce((sum, item) => sum + item.value, 0);
    let cumulative = 0;
    
    const analyzedItems: ABCItem[] = sorted.map(item => {
      cumulative += item.value;
      const percentage = (cumulative / totalValue) * 100;
      const turnover = item.value / item.stock;
      const margin = 20 + Math.random() * 15;
      
      // ABC分類
      let itemClass: 'A' | 'B' | 'C';
      let recommendation: string;
      
      if (percentage <= 70) {
        itemClass = 'A';
        recommendation = '重点管理商品。在庫切れを避け、常時在庫を確保';
      } else if (percentage <= 90) {
        itemClass = 'B';
        recommendation = '通常管理商品。週次で在庫レベルを確認';
      } else {
        itemClass = 'C';
        recommendation = '簡易管理商品。月次確認で十分、在庫削減を検討';
      }
      
      return {
        skuId: item.id,
        skuName: item.name,
        category: item.cat,
        class: itemClass,
        salesValue: item.value,
        salesVolume: item.volume,
        stockValue: item.stock,
        cumulativePercentage: percentage,
        turnover,
        margin,
        recommendation
      };
    });
    
    setAbcItems(analyzedItems);
  };
  
  useEffect(() => {
    generateABCData();
  }, [sortBy]);
  
  const runAnalysis = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      generateABCData();
      setIsAnalyzing(false);
      alert('✅ ABC分析を実行しました！\n\nA品目: 売上の70%を占める重要商品\nB品目: 売上の20%を占める標準商品\nC品目: 売上の10%を占める低回転商品\n\n在庫管理方針を最適化しました。');
    }, 1500);
  };
  
  const filteredItems = selectedClass === 'all' 
    ? abcItems 
    : abcItems.filter(item => item.class === selectedClass);
  
  const getClassColor = (itemClass: 'A' | 'B' | 'C') => {
    switch (itemClass) {
      case 'A': return colors.accent.red;
      case 'B': return colors.accent.blue;
      case 'C': return colors.text.secondary;
    }
  };
  
  const getClassInfo = (itemClass: 'A' | 'B' | 'C') => {
    const items = abcItems.filter(i => i.class === itemClass);
    const totalValue = items.reduce((sum, i) => sum + i.salesValue, 0);
    const totalStock = items.reduce((sum, i) => sum + i.stockValue, 0);
    
    return {
      count: items.length,
      value: totalValue,
      stock: totalStock,
      percentage: abcItems.length > 0 
        ? ((items.length / abcItems.length) * 100).toFixed(1)
        : '0'
    };
  };
  
  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold mb-1" style={{ color: colors.text.primary }}>
              ABC分析
            </h3>
            <p className="text-sm" style={{ color: colors.text.secondary }}>
              売上貢献度による在庫管理の重点化
            </p>
          </div>
          <div className="flex gap-2">
            <Select
              value={sortBy}
              onChange={(value) => setSortBy(value as any)}
              options={[
                { value: 'value', label: '売上金額' },
                { value: 'volume', label: '販売数量' },
                { value: 'turnover', label: '回転率' }
              ]}
            />
            <Button 
              variant="primary" 
              onClick={runAnalysis}
              disabled={isAnalyzing}
            >
              <BarChart3 size={16} className={isAnalyzing ? 'animate-pulse' : ''} />
              {isAnalyzing ? '分析中...' : 'ABC分析実行'}
            </Button>
          </div>
        </div>
        
        {/* クラス別サマリー */}
        <div className="grid grid-cols-3 gap-4">
          {(['A', 'B', 'C'] as const).map(cls => {
            const info = getClassInfo(cls);
            return (
              <div 
                key={cls}
                className="p-4 rounded-lg border-2 cursor-pointer transition-all"
                style={{ 
                  borderColor: selectedClass === cls || selectedClass === 'all' 
                    ? getClassColor(cls) 
                    : colors.ui.border,
                  backgroundColor: selectedClass === cls 
                    ? `${getClassColor(cls)}10` 
                    : colors.ui.bg
                }}
                onClick={() => setSelectedClass(cls)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span 
                    className="text-2xl font-bold"
                    style={{ color: getClassColor(cls) }}
                  >
                    {cls}クラス
                  </span>
                  <Badge 
                    variant={cls === 'A' ? 'error' : cls === 'B' ? 'primary' : 'secondary'}
                  >
                    {info.count}品目
                  </Badge>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span style={{ color: colors.text.secondary }}>売上構成</span>
                    <span style={{ color: colors.text.primary }}>
                      ¥{(info.value / 1000000).toFixed(1)}M
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: colors.text.secondary }}>在庫金額</span>
                    <span style={{ color: colors.text.primary }}>
                      ¥{(info.stock / 1000000).toFixed(1)}M
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: colors.text.secondary }}>品目構成比</span>
                    <span style={{ color: colors.text.primary }}>{info.percentage}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      
      {/* 商品リスト */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium" style={{ color: colors.text.primary }}>
            商品別詳細 ({filteredItems.length}件)
          </h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedClass('all')}
            disabled={selectedClass === 'all'}
          >
            すべて表示
          </Button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: colors.ui.border }}>
                <th className="text-left py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                  SKU
                </th>
                <th className="text-left py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                  商品名
                </th>
                <th className="text-center py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                  クラス
                </th>
                <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                  売上金額
                </th>
                <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                  累積%
                </th>
                <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                  回転率
                </th>
                <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                  粗利率
                </th>
                <th className="text-left py-2 px-3 text-sm font-medium" style={{ color: colors.text.secondary }}>
                  推奨アクション
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, index) => (
                <tr 
                  key={item.skuId}
                  className="border-b hover:bg-gray-50 transition-colors"
                  style={{ borderColor: colors.ui.border }}
                >
                  <td className="py-3 px-3 text-sm font-medium" style={{ color: colors.text.primary }}>
                    {item.skuId}
                  </td>
                  <td className="py-3 px-3 text-sm" style={{ color: colors.text.primary }}>
                    <div>
                      <div>{item.skuName}</div>
                      <div className="text-xs" style={{ color: colors.text.secondary }}>
                        {item.category}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <Badge 
                      variant={item.class === 'A' ? 'error' : item.class === 'B' ? 'primary' : 'secondary'}
                      size="sm"
                    >
                      {item.class}
                    </Badge>
                  </td>
                  <td className="py-3 px-3 text-sm text-right" style={{ color: colors.text.primary }}>
                    ¥{item.salesValue.toLocaleString()}
                  </td>
                  <td className="py-3 px-3 text-sm text-right" style={{ color: colors.text.primary }}>
                    {item.cumulativePercentage.toFixed(1)}%
                  </td>
                  <td className="py-3 px-3 text-sm text-right" style={{ color: colors.text.primary }}>
                    {item.turnover.toFixed(1)}回
                  </td>
                  <td className="py-3 px-3 text-sm text-right" style={{ color: colors.text.primary }}>
                    {item.margin.toFixed(1)}%
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs" style={{ color: colors.text.secondary }}>
                      {item.recommendation}
                    </p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default ABCAnalysis;
